import { useQuery } from '@tanstack/react-query';

import { SupplierSubscriptionPlan } from '../../../../../types/SupplierGroupSubscriptionMessage';
import { Tag } from '../../../../cdl/Tag/Tag';
import { Text } from '../../../../cdl/Text/Text';
import { queries } from '../../../../common/api/queryKeys/queries';
import { TextButton } from '../../../../common/buttons/TextButton';
import { DataSheet } from '../../../../common/DataSheet/DataSheet';
import { GenericError } from '../../../../common/Error/GenericError';
import { formatDate } from '../../../../common/helpers/formatDate.helper';
import { formatNumber } from '../../../../common/helpers/formatNumber.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { useRole } from '../../../../common/hooks/useRole';
import { InfoPopover } from '../../../../common/InfoPopover/InfoPopover';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../../common/ui/Box';
import { SupplierSubscriptionPlanUpgradeCallout } from '../../../../supplier-subscription-plans/components/SupplierSubscriptionPlanUpgradeCallout';

interface SupplierGroupPlanSectionContentProps {
    supplierGroupId: string;
    onViewPlans?: () => void;
}

export const SupplierGroupPlanSectionContent = ({
    supplierGroupId,
    onViewPlans,
}: SupplierGroupPlanSectionContentProps) => {
    const role = useRole();
    const { getHumanReadableValue } = useEnums();
    const subscriptionPlanQuery = useQuery({
        ...queries.supplierGroups.detail(supplierGroupId)._ctx.subscriptionPlan,
        throwOnError: false,
    });
    if (subscriptionPlanQuery.isPending) {
        return <LoadingIndicator />;
    }
    if (subscriptionPlanQuery.isError) {
        return <GenericError variant="basic" />;
    }
    const subscription = subscriptionPlanQuery.data;
    const isBasic = subscription.plan === SupplierSubscriptionPlan.BASIC;
    const renewalAt = subscription.dateSubscriptionRenewal;
    const fee = subscription.transactionFeePercentage;

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Text variant="subtitle1" as="h2">
                {translate('supplierPlan.plansAndSubscription')}
            </Text>
            {!role.isSuperAdmin() && onViewPlans ? (
                <Box display="flex" flexDirection="column" gap={24}>
                    <Box display="flex" flexDirection="column">
                        <Box display="flex" alignItems="center" gap={4} flexWrap="wrap" marginBottom={4}>
                            <Text variant="small" as="h3" fontWeight={500}>
                                {translate(
                                    isBasic
                                        ? 'supplierPlan.unlockMoreFlexibilityVisibilityAndLowerCosts'
                                        : 'supplierPlan.thanksForSubscribing'
                                )}
                            </Text>
                            <Tag
                                variant={
                                    subscription.plan === SupplierSubscriptionPlan.ENTERPRISE ? 'default' : 'accent'
                                }
                                emphasis={subscription.plan === SupplierSubscriptionPlan.ENTERPRISE}
                            >
                                {getHumanReadableValue(
                                    'supplierSubscriptionPlan',
                                    isBasic ? SupplierSubscriptionPlan.PROFESSIONAL : subscription.plan
                                )}
                            </Tag>
                        </Box>
                        {!isBasic ? (
                            <Text variant="small" color="foreground.muted">
                                {renewalAt !== undefined
                                    ? translate('supplierPlan.billedAnnuallyAndNextBillingDate', {
                                          date: formatDate({ date: renewalAt, timeZone: 'UTC' }),
                                      })
                                    : translate('supplierPlan.billedAnnuallyAndRenewsAutomatically')}
                            </Text>
                        ) : null}
                        <Text variant="small" color="foreground.muted">
                            {translate(
                                isBasic
                                    ? 'supplierPlan.multipleOffersSchedulesAndPrioritySupport'
                                    : 'supplierPlan.exploreIncludedFeatures'
                            )}
                            {Characters.NO_BREAK_SPACE}
                            <TextButton onClick={onViewPlans}>
                                <Text variant="small">{translate('supplierSubscriptionPlans.learnMore')}</Text>
                            </TextButton>
                        </Text>
                    </Box>
                    <SupplierSubscriptionPlanUpgradeCallout plan={subscription.plan} />
                </Box>
            ) : null}
            <DataSheet>
                <DataSheet.Label>{translate('supplierPlan.currentPlan')}</DataSheet.Label>
                <Text variant="small">{getHumanReadableValue('supplierSubscriptionPlan', subscription.plan)}</Text>
                {!isBasic ? (
                    <>
                        <DataSheet.Label>{translate('supplierPlan.renewalDate')}</DataSheet.Label>
                        <Text variant="small">
                            {renewalAt !== undefined
                                ? formatDate({ date: renewalAt, timeZone: 'UTC' })
                                : Characters.EN_DASH}
                        </Text>
                    </>
                ) : null}
                <DataSheet.Label>{translate('supplierPlan.transactionFee')}</DataSheet.Label>
                <Box display="flex" alignItems="center" gap={4}>
                    <Text variant="small">
                        {fee !== undefined
                            ? translate('supplierPlan.feePercentage', { percentage: formatNumber({ number: fee }) })
                            : Characters.EN_DASH}
                    </Text>
                    <InfoPopover>{translate('supplierPlan.thisFeeAppliesOnlyToSpotMarketTransactions')}</InfoPopover>
                </Box>
            </DataSheet>
        </Box>
    );
};
